/**
 * Registers the NelDaari service worker.
 *
 * SW is intentionally skipped in dev / preview environments because:
 *   - Webpack HMR + SW pre-caching triggers `controllerchange` on every recompile,
 *     which causes the page to reload in an infinite loop.
 *   - Cached JS chunks can go stale between edits.
 * SW is only registered on the deployed production origin.
 */
import { toast } from "sonner";

const PROD_ORIGINS = ["saveurs-gnf.emergent.host"];

function isProdOrigin() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return PROD_ORIGINS.some((h) => host === h || host.endsWith(`.${h}`));
}

async function unregisterAll() {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const r of regs) {
      const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || "";
      if (url.includes("push-sw.js")) continue; // keep the dedicated push SW (alerts livreur)
      await r.unregister();
    }
    // Also purge any lingering caches the SW created
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.filter((k) => k.startsWith("neldaari-")).map((k) => caches.delete(k)));
    }
  } catch (e) {
    console.warn("SW cleanup failed:", e);
  }
}

export function registerSW() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  if (!isProdOrigin()) {
    // Preview/dev: make sure no stale SW is running (it would cause reload loops)
    unregisterAll();
    return;
  }

  window.addEventListener("load", async () => {
    try {
      const reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });

      // Auto-check for updates every hour when tab is visible
      setInterval(() => {
        if (document.visibilityState === "visible") reg.update();
      }, 60 * 60 * 1000);

      reg.addEventListener("updatefound", () => {
        const newSW = reg.installing;
        if (!newSW) return;
        newSW.addEventListener("statechange", () => {
          if (newSW.state === "installed" && navigator.serviceWorker.controller) {
            toast(
              "Nouvelle version disponible",
              {
                description: "Cliquez pour actualiser.",
                action: {
                  label: "Actualiser",
                  onClick: () => {
                    newSW.postMessage({ type: "SKIP_WAITING" });
                    window.location.reload();
                  },
                },
                duration: 15000,
              }
            );
          }
        });
      });

      // Reload once the new SW takes over (safe in prod because SW file only
      // changes on real deploy)
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });
    } catch (e) {
      console.warn("SW register failed:", e);
    }
  });
}
